import { getDoc, writeBatch, doc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { CompanyUpdate, FinancialItem, InvestmentCompany } from '../types';
import {
  COLLECTION_COMPANY_UPDATES,
  COLLECTION_FINANCIAL_ITEMS,
  COLLECTION_INVESTMENT_ITEMS,
  ownedItemDocId,
} from './firestorePaths';

const LEGACY_WORKSPACE = (userId: string) =>
  doc(db, 'users', userId, 'workspaceState', 'main');

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/**
 * If the old embedded workspace document exists, flattens it into top-level
 * collections and deletes the legacy document.
 */
export async function migrateLegacyWorkspaceStateDocument(userId: string): Promise<void> {
  const legacyRef = LEGACY_WORKSPACE(userId);
  let snap;
  try {
    snap = await getDoc(legacyRef);
  } catch (e) {
    console.warn('Legacy workspace read skipped:', e);
    return;
  }
  if (!snap.exists()) return;

  const d = snap.data();
  const financialItems = (Array.isArray(d.financialItems) ? d.financialItems : []) as FinancialItem[];
  const companies = (Array.isArray(d.companies) ? d.companies : []) as InvestmentCompany[];
  const updates = (Array.isArray(d.updates) ? d.updates : []) as CompanyUpdate[];

  const rows: Array<{ col: string; id: string; payload: Record<string, unknown> }> = [];

  for (const item of financialItems) {
    rows.push({
      col: COLLECTION_FINANCIAL_ITEMS,
      id: ownedItemDocId(userId, item.id),
      payload: { userId, ...item },
    });
  }
  for (const item of companies) {
    rows.push({
      col: COLLECTION_INVESTMENT_ITEMS,
      id: ownedItemDocId(userId, item.id),
      payload: { userId, ...item },
    });
  }
  for (const item of updates) {
    rows.push({
      col: COLLECTION_COMPANY_UPDATES,
      id: ownedItemDocId(userId, item.id),
      payload: { userId, ...item },
    });
  }

  for (const group of chunk(rows, 400)) {
    const batch = writeBatch(db);
    for (const r of group) {
      batch.set(doc(db, r.col, r.id), r.payload);
    }
    await batch.commit();
  }

  await deleteDoc(legacyRef);
}
